import { StateEffect, type EditorState, type Extension, type Range } from '@codemirror/state';
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';
import type { SyntaxNode } from '@lezer/common';
import { Lexer, Parser } from 'marked';
import { activeLineNumbers } from './active-lines';

export const refreshWysiwyg = StateEffect.define<null>();

class BulletWidget extends WidgetType {
  override eq(other: BulletWidget): boolean {
    return other instanceof BulletWidget;
  }

  override toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'cm-mm-bullet';
    span.textContent = '•';
    span.setAttribute('aria-hidden', 'true');
    return span;
  }
}

class ImageWidget extends WidgetType {
  constructor(
    readonly src: string,
    readonly alt: string,
  ) {
    super();
  }

  override eq(other: ImageWidget): boolean {
    return other instanceof ImageWidget && other.src === this.src && other.alt === this.alt;
  }

  override toDOM(): HTMLElement {
    const frame = document.createElement('span');
    frame.className = 'cm-mm-image';
    const image = document.createElement('img');
    image.src = this.src;
    image.alt = this.alt;
    image.decoding = 'async';
    frame.append(image);
    return frame;
  }
}

function hasAncestor(node: SyntaxNode, name: string): boolean {
  for (let current: SyntaxNode | null = node.parent; current; current = current.parent) {
    if (current.name === name) return true;
  }
  return false;
}

function isActive(state: EditorState, activeLines: Set<number>, from: number, to = from): boolean {
  const first = state.doc.lineAt(from).number;
  const last = state.doc.lineAt(Math.max(from, to - 1)).number;
  for (let line = first; line <= last; line++) {
    if (activeLines.has(line)) return true;
  }
  return false;
}

function cleanImageUrl(href: string): string | null {
  try {
    // marked の閲覧レンダラと同じ正規化。既存の %xx は二重エンコードしない。
    return encodeURI(href).replace(/%25(?=[\da-fA-F]{2})/g, '%');
  } catch {
    return null;
  }
}

function decodeHtmlEntities(value: string): string {
  if (!value.includes('&')) return value;
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    // 生のタグとして解釈させず、HTML の文字参照だけをブラウザと同じ規則で復号する。
    textarea.innerHTML = value.replaceAll('<', '&lt;');
    return textarea.value;
  }

  const named: Readonly<Record<string, string>> = {
    amp: '&',
    apos: "'",
    colon: ':',
    gt: '>',
    lt: '<',
    nbsp: '\u00a0',
    quot: '"',
  };
  return value.replace(/&(?:#(\d+)|#x([\da-f]+)|([a-z]+));/gi, (entity, decimal, hex, name) => {
    const codePoint = decimal ? Number(decimal) : hex ? Number.parseInt(hex, 16) : null;
    if (codePoint !== null) {
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return entity;
      }
    }
    return named[String(name).toLowerCase()] ?? entity;
  });
}

function imageDetails(state: EditorState, node: SyntaxNode): { alt: string; src: string } | null {
  const source = state.doc.sliceString(node.from, node.to);
  const tokens = Lexer.lexInline(source, { gfm: true, breaks: true });
  const image = tokens.length === 1 && tokens[0]?.type === 'image' ? tokens[0] : null;
  if (!image || image.raw !== source) return null;

  const src = cleanImageUrl(decodeHtmlEntities(image.href));
  if (!src) return null;
  if (!src.startsWith('/api/images/') && !/^https?:/i.test(src)) return null;
  const parser = new Parser({ gfm: true, breaks: true });
  const rawAlt = image.tokens ? parser.parseInline(image.tokens, parser.textRenderer) : image.text;
  const alt = decodeHtmlEntities(rawAlt);
  return { alt, src };
}

function blockSpacingClass(state: EditorState, node: SyntaxNode): string | null {
  if (node.parent?.name !== 'Document' || !node.prevSibling) return null;
  const between = state.doc.sliceString(node.prevSibling.to, node.from);
  // ソースの空行が既にブロック間隔を担う場合は余白を重ねない。
  if (/\n[\t ]*\n/.test(between)) return null;
  if (node.name === 'ATXHeading1') return 'cm-mm-block-before-6';
  if (node.name === 'ATXHeading2') return 'cm-mm-block-before-5';
  if (/^ATXHeading[3-6]$/.test(node.name)) return 'cm-mm-block-before-4';
  return 'cm-mm-block-before-3';
}

/** 可視範囲のぶんだけ装飾を組む。EditorView に依存しない。 */
export function buildWysiwygDecorations(
  state: EditorState,
  visibleRanges: readonly { from: number; to: number }[],
  activeLines: Set<number>,
): DecorationSet {
  const ranges: Range<Decoration>[] = [];
  const seen = new Set<string>();

  const add = (key: string, decoration: Decoration, from: number, to?: number): void => {
    const identity = `${key}:${from}:${to ?? from}`;
    if (seen.has(identity)) return;
    seen.add(identity);
    ranges.push(to === undefined ? decoration.range(from) : decoration.range(from, to));
  };

  const addLine = (lineNumber: number, className: string): void => {
    const line = state.doc.line(lineNumber);
    add(`line:${className}`, Decoration.line({ class: className }), line.from);
  };

  const addLines = (
    from: number,
    to: number,
    visible: { from: number; to: number },
    className: string,
    edgeClasses = false,
  ): void => {
    const first = state.doc.lineAt(from).number;
    const last = state.doc.lineAt(Math.max(from, to - 1)).number;
    const overlapFrom = Math.max(from, visible.from);
    const overlapTo = Math.min(to, visible.to);
    if (overlapFrom >= overlapTo) return;

    const visibleFirst = state.doc.lineAt(overlapFrom).number;
    const visibleLast = state.doc.lineAt(Math.max(overlapFrom, overlapTo - 1)).number;
    for (let number = visibleFirst; number <= visibleLast; number++) {
      const classes = [className];
      if (edgeClasses && number === first) classes.push(`${className}--first`);
      if (edgeClasses && number === last) classes.push(`${className}--last`);
      addLine(number, classes.join(' '));
    }
  };

  for (const visible of visibleRanges) {
    syntaxTree(state).iterate({
      from: visible.from,
      to: visible.to,
      enter(ref) {
        const { node, from, to } = ref;
        const name = node.name;
        const lineNumber = state.doc.lineAt(from).number;

        if (/^ATXHeading[1-6]$/.test(name)) {
          addLine(lineNumber, `cm-mm-h${name.slice(-1)}`);
          const spacing = blockSpacingClass(state, node);
          if (spacing) addLine(lineNumber, spacing);
          return;
        }

        if (name === 'Paragraph' && !hasAncestor(node, 'ListItem')) {
          const spacing = blockSpacingClass(state, node);
          if (spacing && from >= visible.from) addLine(lineNumber, spacing);
          return;
        }

        if (name === 'HeaderMark' && !isActive(state, activeLines, from, to)) {
          const line = state.doc.lineAt(to);
          const end = to < line.to && state.doc.sliceString(to, to + 1) === ' ' ? to + 1 : to;
          add('header', Decoration.replace({}), from, end);
          return;
        }

        if (name === 'BulletList' || name === 'OrderedList') {
          if (!hasAncestor(node, 'ListItem')) {
            const spacing = blockSpacingClass(state, node);
            if (spacing && from >= visible.from) addLine(lineNumber, spacing);
          }
          return;
        }

        if (name === 'ListItem') {
          if (node.prevSibling?.name === 'ListItem') {
            addLine(lineNumber, 'cm-mm-list-item--following');
          }
          return;
        }

        if (name === 'ListMark') {
          if (hasAncestor(node, 'BulletList') && !isActive(state, activeLines, from, to)) {
            add('bullet', Decoration.replace({ widget: new BulletWidget() }), from, to);
          } else if (hasAncestor(node, 'OrderedList')) {
            add('ordered', Decoration.mark({ class: 'cm-mm-listmark' }), from, to);
          }
          return;
        }

        if (name === 'Blockquote') {
          addLines(from, to, visible, 'cm-mm-quote', true);
          const spacing = blockSpacingClass(state, node);
          if (spacing && from >= visible.from) addLine(lineNumber, spacing);
          return;
        }

        if (name === 'QuoteMark') {
          if (!isActive(state, activeLines, from, to)) {
            const line = state.doc.lineAt(to);
            const end = to < line.to && state.doc.sliceString(to, to + 1) === ' ' ? to + 1 : to;
            add('quote', Decoration.replace({}), from, end);
          } else {
            add('quote-mark', Decoration.mark({ class: 'cm-mm-quote-mark' }), from, to);
          }
          return;
        }

        if (
          (name === 'EmphasisMark' || name === 'StrikethroughMark') &&
          !isActive(state, activeLines, from, to)
        ) {
          add('emphasis', Decoration.replace({}), from, to);
          return;
        }

        if (name === 'InlineCode') {
          const openingMark = node.firstChild;
          const closingMark = node.lastChild;
          if (
            openingMark?.name === 'CodeMark' &&
            closingMark?.name === 'CodeMark' &&
            openingMark.to < closingMark.from
          ) {
            add(
              'inline-code-face',
              Decoration.mark({ class: 'cm-mm-inline-code' }),
              openingMark.to,
              closingMark.from,
            );
          }
          return;
        }

        if (
          name === 'CodeMark' &&
          hasAncestor(node, 'InlineCode') &&
          !isActive(state, activeLines, from, to)
        ) {
          add('inline-code', Decoration.replace({}), from, to);
          return;
        }

        if (name === 'Image') {
          if (!isActive(state, activeLines, from, to)) {
            const details = imageDetails(state, node);
            if (details && state.doc.lineAt(from).number === state.doc.lineAt(to).number) {
              add(
                'image',
                Decoration.replace({ widget: new ImageWidget(details.src, details.alt) }),
                from,
                to,
              );
            }
          }
          // 画像の子はリンクと同じ LinkMark / URL。ここを辿ると、安全でない画像まで
          // リンク装飾として部分的に隠れ、ウィジェットとは置換範囲が重なる。
          return false;
        }

        if ((name === 'LinkMark' || name === 'URL') && !isActive(state, activeLines, from, to)) {
          add('link', Decoration.replace({}), from, to);
          return;
        }

        if (name === 'FencedCode') {
          addLines(from, to, visible, 'cm-mm-code', true);
          return;
        }

        if (name === 'HorizontalRule') {
          addLine(lineNumber, 'cm-mm-hr');
          if (!isActive(state, activeLines, from, to)) {
            addLine(lineNumber, 'cm-mm-hr--rendered');
            add('hr', Decoration.replace({}), from, to);
          }
          return;
        }
      },
    });
  }

  return Decoration.set(ranges, true);
}

/** composing() は CodeMirrorEditor が持つ composition 判定を受け取る。 */
export function wysiwygDecorations(composing: () => boolean): Extension {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildWysiwygDecorations(
          view.state,
          view.visibleRanges,
          activeLineNumbers(view.state, view.hasFocus),
        );
      }

      update(update: ViewUpdate): void {
        if (composing()) {
          this.decorations = this.decorations.map(update.changes);
          return;
        }

        const refresh = update.transactions.some((transaction) =>
          transaction.effects.some((effect) => effect.is(refreshWysiwyg)),
        );
        if (
          update.docChanged ||
          update.viewportChanged ||
          update.selectionSet ||
          update.focusChanged ||
          refresh
        ) {
          this.decorations = buildWysiwygDecorations(
            update.state,
            update.view.visibleRanges,
            activeLineNumbers(update.state, update.view.hasFocus),
          );
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  );
}
